import React, { memo, useEffect, useMemo } from 'react';
import { MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme, SelectFilter } from '@alycecom/ui';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useGetTimezonesQuery } from '@alycecom/services';
import { Timezones } from '@alycecom/modules';

import { getGiftHashId } from '../../../store/data/data.selectors';
import { getTimezone } from '../../../store/giftFlow/giftFlow.selectors';
import { setTimezone } from '../../../store/giftFlow/giftFlow.actions';

const useStyles = makeStyles<AlyceTheme>(() => ({
  timezoneSelect: {
    width: '100%',
    marginLeft: 0,
    textAlign: 'left',
  },
}));

export interface ITimezonesSelectProps {
  isLoading: boolean;
  onSelectTimezone: (timezone: { timezoneName: string }) => void;
}

const { GUESS_TIMEZONE, DEFAULT_TIMEZONE_NAME } = Timezones.CONSTANTS;

const TimezonesSelect = ({ isLoading, onSelectTimezone }: ITimezonesSelectProps): JSX.Element => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const hashId = useSelector(getGiftHashId);

  const { data, isSuccess } = useGetTimezonesQuery(hashId);

  const ids = data?.ids || [];
  const entities = useMemo(() => data?.entities || {}, [data]);

  const timezoneName = useSelector(getTimezone);

  const defaultTimezone = useMemo(() => {
    const timezones = Object.values(entities || {});
    return timezones.findIndex(item => item?.name === GUESS_TIMEZONE) !== -1 ? GUESS_TIMEZONE : DEFAULT_TIMEZONE_NAME;
  }, [entities]);

  useEffect(() => {
    if (!timezoneName) {
      dispatch(setTimezone(defaultTimezone));
    }
  }, [dispatch, timezoneName, defaultTimezone]);

  return (
    <SelectFilter
      dataTestId="Timezone.Select"
      classes={{ root: classes.timezoneSelect }}
      label="Timezone"
      value={isSuccess ? timezoneName : ''}
      name="timezoneName"
      disabled={isLoading}
    >
      {ids?.map(id => (
        <MenuItem
          className={classes.option}
          key={id}
          value={entities[id]?.name}
          onClick={() => {
            onSelectTimezone({ timezoneName: entities[id]?.name || '' });
          }}
        >
          {`(GMT ${moment()
            .tz(entities[id]?.name || '')
            .format('Z')}) ${entities[id]?.title}`}
        </MenuItem>
      ))}
    </SelectFilter>
  );
};

export default memo(TimezonesSelect);
